import { renderRichText, applyLocaleToUrl } from "@/utils/formatters"
import React from "react"
import PropTypes from "prop-types"
import { useRouter } from "next/router"

/**
 * ContactUs Component
 *
 * Renders contact page layout with:
 * - Title & description
 * - Office details (multiple locations)
 * - Resource links
 * - CTA buttons
 *
 * @component
 * @param {Object} props
 * @param {Object} props.data - CMS data object
 * @param {string} props.data.title - Page title
 * @param {string} props.data.description - HTML description
 * @param {Array} props.data.officeDetails - Office data array (Here, we have title, description, cta and contactLabels items)
 * @param {string} props.data.resourceTitle - Title for resources section
 * @param {Array} props.data.resourcesLinks - List of resource links
 * @param {Array} props.data.ctaButtons - CTA buttons
 * @param {string} props.data.officeTitle - Office section heading
 */
function ContactUs({ data }) {
  const { locale } = useRouter()
  const {
    title,
    description,
    officeDetails,
    resourceTitle: resourcesTitle,
    resourcesLinks,
    ctaButtons,
    officeTitle,
  } = data || {}

  const offices = officeDetails?.[0]?.fields?.offices || []

  return (
    <section className="max-w-[980px] mx-auto px-4 py-8">
      {/* Title */}
      <div className="max-w-6xl mx-auto text-center">
        <h1 className="text-[45px] py-10 md:text-[60px] font-[HelveticaLight, Helvetica, sans-serif] text-gray-800">
          {title}
        </h1>
        <div className="mt-10 border-t border-black-300"></div>
      </div>

      {/* Layout */}
      <div className="max-w-6xl mx-auto mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
        {/* LEFT */}
        <div className="md:col-span-8">
          {description && renderRichText(description, locale)}

          <div className="mt-5 text-sm text-gray-800">
            <h3 className="font-semibold text-2xl font-[Helvetica-Light, sans-serif]">
              {officeTitle}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 font-[HelveticaRoman,_Helvetica,_sans-serif]">
              {offices.map((office, oIndex) => {
                const officeFields = office?.fields || {}
                const contactLabels = officeFields?.contactLabel || []
                const cta = officeFields?.ctaButton?.[0]?.fields

                let positionClass = ""
                if (oIndex === 0) positionClass = "md:col-start-1"
                else if (oIndex === 1) positionClass = "md:col-start-2"
                else if (oIndex % 2 === 0) positionClass = "md:col-start-2"
                else positionClass = "md:col-start-1"

                return (
                  <div
                    key={office?.sys?.id || oIndex}
                    className={`${positionClass} border-t border-gray-300 mt-8 pt-8 min-h-[275px]`}
                  >
                    <h4 className="font-semibold text-sm mb-2 font-[HelveticaBold,Helvetica,sans-serif]">
                      {officeFields?.title}
                    </h4>

                    {officeFields?.details && (
                      <div className="text-sm pr-[30px] pb-[10px]">
                        {renderRichText(officeFields.details, locale)}
                      </div>
                    )}

                    <div className="text-sm font-[HelveticaRoman,_Helvetica,_sans-serif]">
                      {contactLabels.map((item, i) => {
                        const itemFields = item?.fields || {}

                        if (itemFields.description) {
                          return (
                            <p
                              key={item?.sys?.id || i}
                              className="font-[HelveticaRoman, Helvetica, sans-serif] pr-[30px] pb-[10px]"
                            >
                              {renderRichText(itemFields.description, locale)}
                            </p>
                          )
                        } else {
                          return (
                            <>
                              {itemFields?.exceptionalText && (
                                <span>{itemFields.exceptionalText}</span>
                              )}
                              <p
                                key={item?.sys?.id || i}
                                className="font-[HelveticaRoman, Helvetica, sans-serif]"
                              >
                                <b>{itemFields?.label} : </b>
                                <a
                                  href={applyLocaleToUrl(
                                    itemFields?.href,
                                    locale
                                  )}
                                  className="text-blue-600 no-underline hover:!no-underline"
                                >
                                  {itemFields?.value}
                                </a>
                              </p>
                            </>
                          )
                        }
                      })}
                    </div>

                    {cta && (
                      <a
                        href={applyLocaleToUrl(cta?.url, locale)}
                        className="
                          inline-block
                          bg-[#337ab7]
                          text-white
                          text-[0.8em]
                          px-[10px] py-[5px]
                          text-center
                          uppercase
                          no-underline
                          hover:!no-underline
                          !text-white
                          rounded-[3px]
                          shadow-[0_2px_2px_rgba(0,0,0,0.2)]
                          mt-3
                          transition-colors hover:bg-[#000] hover:text-[#ccc]
                        "
                      >
                        {cta?.label}
                      </a>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex flex-col md:col-span-4">
          <div className="block md:hidden my-6 border-t border-gray-300"></div>

          <h3 className="text-base md:text-2xl font-semibold text-gray-800 uppercase tracking-wide">
            {resourcesTitle}
          </h3>

          <ul className="mt-3 mb-8 text-sm list-disc font-[HelveticaRoman,_Helvetica,_sans-serif]">
            {resourcesLinks?.map((link, index) => {
              const linkFields = link?.fields || {}
              return (
                <li key={link?.sys?.id || index} className="ml-12">
                  <a
                    href={applyLocaleToUrl(linkFields?.href, locale)}
                    className="text-blue-800 hover:!no-underline text-[14px] color-[#337ab7]"
                  >
                    {linkFields?.label}
                  </a>
                </li>
              )
            })}
          </ul>

          <div className="mt-6 space-y-4">
            {ctaButtons?.map((btn, index) => {
              const btnFields = btn?.fields || {}
              return (
                <a
                  key={btn?.sys?.id || index}
                  href={applyLocaleToUrl(btnFields?.href, locale)}
                  className="block w-full text-center bg-[#364573] text-white py-3 rounded no-underline !text-white hover:!no-underline"
                >
                  {btnFields?.label}
                </a>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

/* =========================
   PropTypes
========================= */

const SysType = PropTypes.shape({
  id: PropTypes.string,
})

const CTAType = PropTypes.shape({
  sys: SysType,
  fields: PropTypes.shape({
    label: PropTypes.string,
    url: PropTypes.string,
    href: PropTypes.string,
  }),
})

const ContactLabelType = PropTypes.shape({
  sys: SysType,
  fields: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    href: PropTypes.string,
  }),
})

const OfficeType = PropTypes.shape({
  sys: SysType,
  fields: PropTypes.shape({
    title: PropTypes.string,
    details: PropTypes.any,
    contactLabel: PropTypes.arrayOf(ContactLabelType),
    ctaButton: PropTypes.arrayOf(CTAType),
  }),
})

ContactUs.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    officeTitle: PropTypes.string,
    resourceTitle: PropTypes.string,
    officeDetails: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          offices: PropTypes.arrayOf(OfficeType),
        }),
      })
    ),
    resourcesLinks: PropTypes.arrayOf(
      PropTypes.shape({
        sys: SysType,
        fields: PropTypes.shape({
          label: PropTypes.string,
          href: PropTypes.string,
        }),
      })
    ),
    ctaButtons: PropTypes.arrayOf(CTAType),
  }),
}

export default ContactUs
